import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import EventBus from 'eventing-bus';
import { Tabs, Tab, Slider, TextField, FormControlLabel, Switch, Tooltip as MuiTooltip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getEmailSettings, getIntegrations } from '../../store/Integration';
import { getAllCampaigns } from '../../store/Campaign';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import './EmailSettingsModal.css';

const DEFAULT_SMS_SETTINGS = { dailySendingLimit: 50, minWaitTime: 1 };

const QUILL_MODULES = { toolbar: [ ['bold', 'italic', 'underline', 'strike'], [{ color: [] }], ['link', 'image'], ['clean'], ] };

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'strike', 'color', 'link', 'image'];

const DEFAULT_SETTINGS = {
    warmup: { enabled: false, paused: false, increasePerDay: 2, replyRate: 30, dailyLimit: 40, readEmulation: true, warmupTag: 'warmup', weekdaysOnly: false, warmCustomTrackingDomain: false, openRate: 100, spamProtection: 100, markImportant: 100, slowWarmup: true },
    campaign: { dailySendingLimit: 50, minWaitTime: 1, slowRamp: true, replyToAddress: '', placementTestLimit: 10 },
    sender: { firstName: '', lastName: '', signature: '' },
    stats: { sent: 0, received: 0, savedFromSpam: 0, inboxRate: 0, openRate: 0, replyRate: 0, lastUpdated: null },
    warmupState: { sentToday: 0, sentTodayDate: null }
};

const EmailSettingsModal = ({ isOpen, toggle, integration, bulkIntegrationIds }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { emailSettings, channelSettings } = useSelector(state => state['Integration']);
    const { allCampaigns, campaignsLoading } = useSelector(state => state['Campaign']);
    const currentWorkspace = useSelector(state => state['Settings']?.['currentWorkspace']);
    const [activeTab, setActiveTab] = useState(0);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [smsSettings, setSmsSettings] = useState(DEFAULT_SMS_SETTINGS);
    const [saving, setSaving] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [localDirty, setLocalDirty] = useState(false);

    const isSms = (integration?.['toolId']?.['name'] || integration?.['name'] || '').toLowerCase() === 'twilio';

    // Load settings when panel opens, and reset dirty flag
    useEffect(() => {
        if (isOpen && integration?.['_id']) {
            setLocalDirty(false);
            if (!isSms) {
                dispatch(getEmailSettings(integration['_id']));
                dispatch(getAllCampaigns({ limit: 200 }));
            }
        }
    }, [isOpen, integration?.['_id'], isSms, dispatch]);

    // Campaigns that reference this integration via accountIntegrations: { salesAccountId: [integrationId, ...] }
    const linkedCampaigns = (allCampaigns || []).filter(c => {
        const accountIntegrations = c['accountIntegrations'] || {};
        return Object.values(accountIntegrations).some(ids => {
            const idList = Array.isArray(ids) ? ids : [ids];
            return idList.some(id => {
                const idStr = typeof id === 'string' ? id : id?.['_id'] || '';
                return idStr === integration?.['_id'];
            });
        });
    });

    const handleViewCampaign = (campaignId) => {
        toggle();
        history.push(getWorkspacePath(`/campaigns/${campaignId}`, currentWorkspace?.['_id']));
    };

    const handleAddCampaign = () => {
        toggle();
        history.push(getWorkspacePath('/campaigns', currentWorkspace?.['_id']));
    };

    // Populate settings from Redux — only when user hasn't made local changes
    useEffect(() => {
        if (localDirty) return;
        if (isSms) {
            // Prefer channelSettings Redux store (post-save), fall back to integration.smsSettings from server
            const stored = channelSettings?.[integration?.['_id']]?.['sms'] || integration?.['smsSettings'] || {};
            setSmsSettings({ dailySendingLimit: stored['dailySendingLimit'] ?? DEFAULT_SMS_SETTINGS['dailySendingLimit'], minWaitTime: stored['minWaitTime'] ?? DEFAULT_SMS_SETTINGS['minWaitTime'] });
        } else if (emailSettings?.['data']?.[integration?.['_id']]) {
            const stored = emailSettings['data'][integration['_id']];
            setSettings({
                warmup: { ...DEFAULT_SETTINGS['warmup'], ...(stored['warmup'] || {}) },
                campaign: { ...DEFAULT_SETTINGS['campaign'], ...(stored['campaign'] || {}) },
                sender: { ...DEFAULT_SETTINGS['sender'], ...(stored['sender'] || {}) },
                stats: { ...DEFAULT_SETTINGS['stats'], ...(stored['stats'] || {}) },
                warmupState: stored['warmupState'] || { sentToday: 0, sentTodayDate: null }
            });
        }
    }, [emailSettings, channelSettings, integration?.['_id'], integration?.['smsSettings'], isSms, localDirty]);

    const updateWarmup = (key, value) => {
        setLocalDirty(true);
        setSettings(prev => ({ ...prev, warmup: { ...prev['warmup'], [key]: value } }));
    };
    const updateCampaign = (key, value) => {
        setLocalDirty(true);
        setSettings(prev => ({ ...prev, campaign: { ...prev['campaign'], [key]: value } }));
    };

    const handleSave = () => {
        setSaving(true);
        const integrationId = bulkIntegrationIds?.[0] || integration?.['_id'];
        if (isSms) {
            dispatch({
                type: 'UPDATE_CHANNEL_SETTINGS',
                payload: { integrationId, channel: 'sms', settings: smsSettings, onSuccess: () => { setSaving(false); dispatch(getIntegrations()); dispatch({ type: 'GET_SALES_ACCOUNTS' }); toggle(); } }
            });
            return;
        }
        const payload = { warmup: settings['warmup'], campaign: settings['campaign'], sender: settings['sender'] };
        const isBulk = bulkIntegrationIds?.length > 1;
        if (isBulk) {
            dispatch({
                type: 'UPDATE_EMAIL_SETTINGS_BULK',
                payload: {
                    integrationIds: bulkIntegrationIds,
                    settings: payload,
                    onSuccess: () => { setSaving(false); dispatch(getIntegrations()); toggle(); }
                }
            });
        } else {
            dispatch({
                type: 'UPDATE_EMAIL_SETTINGS',
                payload: { integrationId, settings: payload, onSuccess: () => { setSaving(false); dispatch(getIntegrations()); dispatch(getEmailSettings(integrationId)); toggle(); } }
            });
        }
    };

    // Warmup control handlers
    const handleEnableWarmup = () => {
        setLocalDirty(true);
        setSettings(prev => ({
            ...prev,
            warmup: {
                ...prev['warmup'],
                enabled: true,
                startDate: prev['warmup']['startDate'] || new Date().toISOString(),
            }
        }));
    };
    const handleDisableWarmup = () => updateWarmup('enabled', false);
    const handlePauseWarmup = () => updateWarmup('paused', !settings['warmup']['paused']);

    const accountEmail = integration?.['tokens']?.['email'] || integration?.['accountIdentifier'] || integration?.['name'] || 'Email Account';

    // Get warmup status
    const getWarmupStatus = () => {
        if (!settings['warmup']['enabled']) return { text: 'Disabled', color: 'disabled' };
        if (settings['warmup']['paused']) return { text: 'Paused', color: 'paused' };
        return { text: 'Active', color: 'active' };
    };
    const warmupStatus = getWarmupStatus();

    // Chart data — last 7 days from sentHistory + today's live count from warmupState
    const buildChartData = () => {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const today = new Date();
        const todayStr = today.toISOString().slice(0, 10);
        const history = Array.isArray(settings['stats']['sentHistory']) ? settings['stats']['sentHistory'] : [];
        const historyMap = {};
        history.forEach(item => { if (item.date) historyMap[item.date] = item.count || 0; });
        // Override today's bar with the live sentToday counter
        const sentToday = settings['warmupState']?.['sentToday'] || 0;
        if (sentToday > 0) historyMap[todayStr] = sentToday;
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(today);
            d.setDate(today.getDate() - (6 - i));
            const dateStr = d.toISOString().slice(0, 10);
            return { name: dayNames[d.getDay()], value: historyMap[dateStr] || 0 };
        });
    };
    const chartData = buildChartData();

    if (!isOpen) return null;

    return (
        <div className="email-settings-modal">
            {/* Backdrop */}
            <div className="esm-backdrop" onClick={toggle}></div>

            {/* Slider Panel */}
            <div className={`esm-slider-panel ${isOpen ? 'open' : ''}`}>
                {/* Header */}
                <div className="esm-panel-header">
                    <div className="esm-header-content">
                        <i className="icon">
                            <svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.6632 7.07544L10.1556 8.58807C9.5443 9.20155 8.46866 9.21477 7.8441 8.58807L6.3365 7.07544L0.921875 12.5076C1.12343 12.6008 1.34565 12.6565 1.5819 12.6565H16.4178C16.6541 12.6565 16.8762 12.6009 17.0777 12.5077L11.6632 7.07544Z" fill="#120348" />
                                <path d="M16.418 0H1.58203C1.34578 0 1.12356 0.0556172 0.922078 0.148816L6.70799 5.95392C6.70837 5.95431 6.70883 5.95438 6.70922 5.95477C6.70946 5.95502 6.70962 5.95534 6.70968 5.95568L8.59099 7.84322C8.79082 8.04305 9.20925 8.04305 9.40908 7.84322L11.29 5.956C11.29 5.956 11.2905 5.95515 11.2909 5.95477C11.2909 5.95477 11.2917 5.95431 11.2921 5.95392L17.0779 0.148781C16.8764 0.055547 16.6542 0 16.418 0ZM0.168258 0.886359C0.0639844 1.09723 0 1.3313 0 1.58203V11.0742C0 11.325 0.0639141 11.559 0.168223 11.7699L5.59223 6.3283L0.168258 0.886359ZM17.8317 0.886289L12.4078 6.3283L17.8317 11.77C17.936 11.5591 18 11.325 18 11.0742V1.58203C18 1.33123 17.936 1.09716 17.8317 0.886289Z" fill="#120348" />
                            </svg>
                        </i>
                        <span>{accountEmail}</span>
                    </div>
                    <button className="esm-close-btn" onClick={toggle}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                {/* Warmup Status & Controls — email only */}
                {!isSms && (
                    <div className="esm-warmup-controls">
                        <div className="esm-warmup-status">
                            <span className="esm-status-label">Warmup Status:</span>
                            <span className={`esm-status-badge ${warmupStatus['color']}`}>{warmupStatus['text']}</span>
                        </div>
                        <div className="esm-warmup-buttons">
                            {!settings['warmup']['enabled'] ? (
                                <button className="btn-style-new enable" onClick={handleEnableWarmup}>
                                    <i className="fa fa-play icon"></i> Enable
                                </button>
                            ) : (
                                <>
                                    <button className="btn-style-border" onClick={handlePauseWarmup}>
                                        <i className={`fa fa-${settings['warmup']['paused'] ? 'play' : 'pause'} icon`}></i>
                                        {settings['warmup']['paused'] ? 'Resume' : 'Pause'}
                                    </button>
                                    <button className="btn-style-border" onClick={handleDisableWarmup}>
                                        <i className="fa fa-stop icon"></i> Disable
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <Tabs value={isSms ? 0 : activeTab} onChange={isSms ? undefined : (e, v) => setActiveTab(v)} className="esm-tabs">
                    {isSms ? <Tab label="SMS Settings" /> : null}
                    {!isSms ? <Tab label="Warmup" /> : null}
                    {!isSms ? <Tab label="Campaign Settings" /> : null}
                    {!isSms ? <Tab label="Signature" /> : null}
                </Tabs>

                {/* Panel Content */}
                <div className="esm-panel-content">

                    {/* ── SMS Settings Tab ── */}
                    {isSms && (
                        <div className="esm-tab-content">
                            <div className="esm-warmup-settings-heading">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>SMS Settings</span>
                            </div>
                            <div className="esm-basic-section" style={{ marginTop: 12 }}>
                                <div className="esm-slider-grid">
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Daily sending limit</h4>
                                            <span className="esm-suggested">Max SMS messages per day</span>
                                        </div>
                                        <div className="esm-campaign-field-row">
                                            <TextField hiddenLabel type="text" value={smsSettings['dailySendingLimit']}
                                                onChange={(e) => { setLocalDirty(true); setSmsSettings(prev => ({ ...prev, dailySendingLimit: Number(e.target.value) || 0 })); }}
                                                size="small" className="esm-campaign-number-field" />
                                            <span className="esm-campaign-field-unit">messages</span>
                                        </div>
                                    </div>
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Minimum wait time</h4>
                                            <span className="esm-suggested">Cooldown between messages</span>
                                        </div>
                                        <div className="esm-campaign-field-row">
                                            <TextField hiddenLabel type="text" value={smsSettings['minWaitTime']}
                                                onChange={(e) => { setLocalDirty(true); setSmsSettings(prev => ({ ...prev, minWaitTime: Number(e.target.value) || 0 })); }}
                                                size="small" className="esm-campaign-number-field" />
                                            <span className="esm-campaign-field-unit">minute(s)</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Warmup Tab ── */}
                    {!isSms && activeTab === 0 && (
                        <div className="esm-tab-content">

                            {/* Started date */}
                            {settings['warmup']['startDate'] && (() => {
                                const start = new Date(settings['warmup']['startDate']);
                                const daysAgo = Math.floor((Date.now() - start.getTime()) / 86400000);
                                return (
                                    <div className="esm-started-row">
                                        <span className="esm-started-icon">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#6b7280" strokeWidth="2"/><path d="M12 6v6l4 2" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span className="esm-started-text">
                                            Started on <strong>{start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>
                                            <span className="esm-started-days"> | {daysAgo} day{daysAgo !== 1 ? 's' : ''} ago</span>
                                        </span>
                                    </div>
                                );
                            })()}

                            {/* Card 1 — Summary */}
                            <div className="esm-warmup-card">
                                <div className="esm-warmup-card-header">
                                    <span className="esm-warmup-card-title">Summary for past week</span>
                                </div>
                                <div className="esm-summary-grid">
                                    <div className="esm-summary-item">
                                        <span className="esm-summary-number">{settings['stats']['received']}</span>
                                        <span className="esm-summary-label">warmup emails received</span>
                                    </div>
                                    <div className="esm-summary-item">
                                        <span className="esm-summary-number">{settings['stats']['sent']}</span>
                                        <span className="esm-summary-label">warmup emails sent</span>
                                    </div>
                                    <div className="esm-summary-item">
                                        <span className="esm-summary-number">{settings['stats']['savedFromSpam']}</span>
                                        <span className="esm-summary-label">saved from spam</span>
                                    </div>
                                    <div className="esm-summary-item">
                                        <span className="esm-summary-number">{settings['warmup']['markImportant']}%</span>
                                        <span className="esm-summary-label">mark as important</span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2 — Chart */}
                            <div className="esm-warmup-card esm-warmup-chart-card">
                                <div className="esm-warmup-card-header">
                                    <span className="esm-warmup-card-title">Warmup Emails Sent</span>
                                </div>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={chartData} barSize={36} margin={{ top: 24, right: 16, left: -10, bottom: 0 }}>
                                        <XAxis dataKey="name" axisLine={{ stroke: '#e5e7eb' }} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                                        <YAxis allowDecimals={false} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} width={40} />
                                        <Tooltip
                                            cursor={false}
                                            contentStyle={{ border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12 }}
                                            formatter={(value) => [value, 'Sent']}
                                        />
                                        <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="var(--primary-color)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Warmup Settings heading */}
                            <div className="esm-warmup-settings-heading">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2C9.5 6 7 8.5 7 13a5 5 0 0 0 10 0c0-4.5-2.5-7-5-11z" stroke="#9ca3af" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
                                    <path d="M12 13c0-2 1-3.5 2-5-.5 2-.5 3.5 0 5a2 2 0 1 1-2 0z" fill="#9ca3af"/>
                                </svg>
                                <span>Warmup Settings</span>
                            </div>

                            {/* Basic Config — moved from Settings tab */}
                            <div className="esm-basic-section" style={{ marginTop: 12 }}>
                                <div className="esm-slider-grid style-two" style={{ marginBottom: 12 }}>
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-info">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <h4 style={{ margin: 0, width: 'fit-content' }}>Slow warmup</h4>
                                                <MuiTooltip placement="top" arrow title={
                                                    settings?.warmup?.slowWarmup !== false
                                                        ? <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                                                            <div><strong>Ramp-up mode ON</strong></div>
                                                            <div>Increase per day: <strong>{settings['warmup']['increasePerDay'] || 2}</strong></div>
                                                            <div>Daily limit: <strong>{settings['warmup']['dailyLimit'] || 40}</strong></div>
                                                            <div>Formula: starts at 2, adds {settings['warmup']['increasePerDay'] || 2} each day</div>
                                                            <div>until it reaches {settings['warmup']['dailyLimit'] || 40} emails/day</div>
                                                          </div>
                                                        : <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                                                            <div><strong>Ramp-up mode OFF</strong></div>
                                                            <div>Sends <strong>{settings['warmup']['dailyLimit'] || 40}</strong> emails daily</div>
                                                            <div>at full limit from day one</div>
                                                          </div>
                                                }>
                                                    <i className="fa fa-info-circle" style={{ fontSize: 13, color: '#9ca3af', cursor: 'pointer' }}></i>
                                                </MuiTooltip>
                                            </div>
                                            <p>Gradually increase sending volume each day using ramp-up schedule</p>
                                        </div>
                                        <FormControlLabel control={
                                            <Switch checked={settings?.warmup?.slowWarmup !== false}
                                                onChange={(e) => updateWarmup('slowWarmup', e.target.checked)}
                                                size="small" className="csetup-switch" />
                                        } />
                                    </div>
                                </div>

                                <div className="esm-slider-grid">
                                    {settings['warmup']['slowWarmup'] !== false && <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Increase per day: {settings['warmup']['increasePerDay']}</h4>
                                            <span className="esm-suggested">Suggested 1</span>
                                        </div>
                                        <TextField fullWidth hiddenLabel type="text" value={settings['warmup']['increasePerDay']}
                                            onChange={(e) => updateWarmup('increasePerDay', e.target.value)}
                                            placeholder="Enter value" size="small" />
                                    </div>}
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Reply rate: {settings['warmup']['replyRate']}%</h4>
                                            <span className="esm-suggested">Suggested 30</span>
                                        </div>
                                        <TextField fullWidth hiddenLabel type="text" value={settings['warmup']['replyRate']}
                                            onChange={(e) => updateWarmup('replyRate', e.target.value)}
                                            placeholder="Enter value" size="small"
                                            InputProps={{ endAdornment: <span style={{ color: '#000000ff', fontSize: 16 }}>%</span> }} />
                                    </div>
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Daily warmup limit: {settings['warmup']['dailyLimit']}</h4>
                                            <span className="esm-suggested">Suggested 10</span>
                                        </div>
                                        <TextField fullWidth hiddenLabel type="text" value={settings['warmup']['dailyLimit']}
                                            onChange={(e) => updateWarmup('dailyLimit', parseInt(e.target.value) || 5)}
                                            placeholder="Enter value" size="small" />
                                    </div>
                                </div>

                                <div className="esm-slider-grid style-two">
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-info">
                                            <h4>Read emulation</h4>
                                            <p>Simulate reading emails to improve engagement metrics</p>
                                        </div>
                                        <FormControlLabel control={
                                            <Switch checked={settings?.warmup?.readEmulation || false}
                                                onChange={(e) => updateWarmup('readEmulation', e.target.checked)}
                                                size="small" className="csetup-switch" />
                                        } />
                                    </div>
                                </div>

                                <div className="esm-slider-grid style-two">
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-info">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <h4 style={{ margin: 0, width: 'fit-content' }}>Warmup tag (for filtering)</h4>
                                                <MuiTooltip placement="top" arrow title={<div style={{ fontSize: 12, lineHeight: 1.6 }}>This unique label will be inserted in all of your warmup emails</div>}>
                                                    <i className="fa fa-info-circle" style={{ fontSize: 13, color: '#9ca3af', cursor: 'pointer' }}></i>
                                                </MuiTooltip>
                                            </div>
                                        </div>
                                        <TextField fullWidth hiddenLabel type="text" value={settings['warmup']['warmupTag']}
                                            onChange={(e) => updateWarmup('warmupTag', e.target.value)}
                                            placeholder="warmup" size="small" />
                                    </div>
                                </div>
                            </div>

                            {/* Advanced Settings Toggle */}
                            <button className="esm-advanced-toggle" onClick={() => setShowAdvanced(prev => !prev)}>
                                <i className="fa fa-fire"></i>
                                Advanced Settings
                                <i className={`fa fa-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
                            </button>

                            {/* Advanced Settings */}
                            {showAdvanced && <div className="esm-advanced-section" style={{ display: 'block', marginTop: 12, borderTop: '1px solid #f3f4f6', paddingTop: 16 }}>
                                <div className="esm-advanced-header" style={{ display: 'none' }}>
                                    <i className="fa fa-fire"></i>
                                    <h4>Advanced Settings</h4>
                                </div>

                                {/* Weekdays only */}
                                <div className="esm-advanced-row">
                                    <div className="esm-advanced-info">
                                        <h5 className="esm-advanced-label">Weekdays only</h5>
                                        <p className="esm-advanced-desc">Only send warmup emails on weekdays for a more natural sending pattern</p>
                                    </div>
                                    <FormControlLabel control={
                                        <Switch checked={settings?.warmup?.weekdaysOnly || false}
                                            onChange={(e) => updateWarmup('weekdaysOnly', e.target.checked)}
                                            size="small" className="csetup-switch" />
                                    } />
                                </div>

                                {/* Warm custom tracking domain */}
                                <div className="esm-advanced-row">
                                    <div className="esm-advanced-info">
                                        <h5 className="esm-advanced-label">Warm custom tracking domain</h5>
                                        <p className="esm-advanced-desc">Include your custom tracking domain in warmup emails to improve deliverability</p>
                                    </div>
                                    <FormControlLabel control={
                                        <Switch checked={Boolean(settings?.warmup?.warmCustomTrackingDomain)}
                                            onChange={(e) => updateWarmup('warmCustomTrackingDomain', e.target.checked)}
                                            size="small" className="csetup-switch" />
                                    } />
                                </div>

                                {/* Open Rate */}
                                <div className="esm-advanced-row esm-advanced-slider-row">
                                    <div className="esm-advanced-info">
                                        <h5 className="esm-advanced-label">Open Rate</h5>
                                        <p className="esm-advanced-desc">How many of your warmup emails to open</p>
                                    </div>
                                    <div className="esm-advanced-slider-wrapper">
                                        <Slider value={settings['warmup']['openRate']} onChange={(e, v) => updateWarmup('openRate', v)} min={0} max={100} valueLabelDisplay="auto" />
                                        <span className="esm-slider-value">{settings['warmup']['openRate']}%</span>
                                    </div>
                                </div>

                                {/* Spam Protection */}
                                <div className="esm-advanced-row esm-advanced-slider-row">
                                    <div className="esm-advanced-info">
                                        <h5 className="esm-advanced-label">Spam Protection</h5>
                                        <p className="esm-advanced-desc">How many of your warmup emails to save from spam folder</p>
                                    </div>
                                    <div className="esm-advanced-slider-wrapper">
                                        <Slider value={settings['warmup']['spamProtection']} onChange={(e, v) => updateWarmup('spamProtection', v)} min={0} max={100} valueLabelDisplay="auto" />
                                        <span className="esm-slider-value">{settings['warmup']['spamProtection']}%</span>
                                    </div>
                                </div>

                                {/* Mark Important */}
                                <div className="esm-advanced-row esm-advanced-slider-row">
                                    <div className="esm-advanced-info">
                                        <h5 className="esm-advanced-label">Mark Important</h5>
                                        <p className="esm-advanced-desc">How many of your warmup emails to mark as important</p>
                                    </div>
                                    <div className="esm-advanced-slider-wrapper">
                                        <Slider value={settings['warmup']['markImportant']} onChange={(e, v) => updateWarmup('markImportant', v)} min={0} max={100} valueLabelDisplay="auto" />
                                        <span className="esm-slider-value">{settings['warmup']['markImportant']}%</span>
                                    </div>
                                </div>

                            </div>}
                        </div>
                    )}

                    {/* ── Settings Tab ── */}
                    {!isSms && activeTab === 2 && (
                        <div className="esm-tab-content">

                            {/* Sender Name */}
                            <div className="esm-settings-section">
                                <div className="esm-settings-section-title">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#6b7280" strokeWidth="2"/><path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg>
                                    Sender name
                                </div>
                                <div className="esm-sender-name-grid">
                                    <div className="esm-sender-name-field">
                                        <label className="esm-field-label">First Name</label>
                                        <TextField fullWidth hiddenLabel type="text"
                                            value={settings['sender']?.['firstName'] || ''}
                                            onChange={(e) => setSettings(prev => ({ ...prev, sender: { ...prev['sender'], firstName: e.target.value } }))}
                                            placeholder="First name" size="small" />
                                    </div>
                                    <div className="esm-sender-name-field">
                                        <label className="esm-field-label">Last Name</label>
                                        <TextField fullWidth hiddenLabel type="text"
                                            value={settings['sender']?.['lastName'] || ''}
                                            onChange={(e) => setSettings(prev => ({ ...prev, sender: { ...prev['sender'], lastName: e.target.value } }))}
                                            placeholder="Last name" size="small" />
                                    </div>
                                </div>
                            </div>

                            {/* Signature */}
                            <div className="esm-settings-section">
                                <div className="esm-settings-section-title">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 17l4-4 4 4 6-8 4 2" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    Signature
                                </div>
                                <div className="esm-signature-editor">
                                    <ReactQuill
                                        theme="snow"
                                        value={settings['sender']?.['signature'] || ''}
                                        onChange={(value) => setSettings(prev => ({ ...prev, sender: { ...prev['sender'], signature: value } }))}
                                        modules={QUILL_MODULES}
                                        formats={QUILL_FORMATS}
                                        placeholder="Write your email signature here..."
                                    />
                                </div>
                            </div>

                        </div>
                    )}

                    {/* ── Campaigns Tab ── */}
                    {!isSms && activeTab === 1 && (
                        <div className="esm-tab-content">

                            {/* Campaign Settings heading */}
                            <div className="esm-warmup-settings-heading">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22 2L11 13" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>Campaign Settings</span>
                            </div>

                            <div className="esm-basic-section" style={{ marginTop: 12 }}>
                                {/* Row 1: Daily campaign limit + Minimum wait time */}
                                <div className="esm-slider-grid">
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Daily campaign limit</h4>
                                            <span className="esm-suggested">Daily sending limit</span>
                                        </div>
                                        <div className="esm-campaign-field-row">
                                            <TextField hiddenLabel type="text" value={settings['campaign']['dailySendingLimit']}
                                                onChange={(e) => updateCampaign('dailySendingLimit', Number(e.target.value) || 0)}
                                                size="small" className="esm-campaign-number-field" />
                                            <span className="esm-campaign-field-unit">emails</span>
                                        </div>
                                    </div>
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Minimum wait time</h4>
                                            <span className="esm-suggested">When used with multiple campaigns</span>
                                        </div>
                                        <div className="esm-campaign-field-row">
                                            <TextField hiddenLabel type="text" value={settings['campaign']['minWaitTime']}
                                                onChange={(e) => updateCampaign('minWaitTime', Number(e.target.value) || 0)}
                                                size="small" className="esm-campaign-number-field" />
                                            <span className="esm-campaign-field-unit">minute(s)</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Row 2: Campaign slow ramp + Reply-to Address */}
                                <div className="esm-slider-grid" style={{ marginTop: 16 }}>
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Campaign slow ramp</h4>
                                            <span className="esm-suggested">Gradually increase the number of campaign emails sent per day</span>
                                        </div>
                                        <div className="esm-campaign-slowramp-row">
                                            <div className="esm-campaign-switch-row">
                                                <span className="esm-campaign-field-unit">Enable</span>
                                                <FormControlLabel control={
                                                    <Switch checked={Boolean(settings['campaign']['slowRamp'])}
                                                        onChange={(e) => updateCampaign('slowRamp', e.target.checked)}
                                                        size="small" className="csetup-switch" />
                                                } label="" />
                                            </div>
                                            {settings['campaign']['slowRamp'] && (
                                                <span className="esm-campaign-recommended">Recommended</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="esm-slider-item esm-reply-to-row">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Reply-to Address</h4>
                                            <span className="esm-suggested">Set a reply-to email address (optional)</span>
                                        </div>
                                        <TextField fullWidth hiddenLabel type="email"
                                            value={settings['campaign']['replyToAddress'] || ''}
                                            onChange={(e) => updateCampaign('replyToAddress', e.target.value)}
                                            placeholder="" size="small" />
                                    </div>
                                </div>

                                {/* Row 3: Daily Inbox Placement test limit */}
                                <div className="esm-slider-grid" style={{ marginTop: 16 }}>
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-label-wrap">
                                            <h4>Daily Inbox Placement test limit</h4>
                                            <span className="esm-suggested">Maximum number of inbox placement tests per day</span>
                                        </div>
                                        <div className="esm-campaign-field-row">
                                            <TextField hiddenLabel type="text" value={settings['campaign']['placementTestLimit']}
                                                onChange={(e) => updateCampaign('placementTestLimit', Number(e.target.value) || 0)}
                                                size="small" className="esm-campaign-number-field" />
                                            <span className="esm-campaign-field-unit">test emails</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="esm-campaigns-divider"></div>

                            {/* Linked campaigns heading */}
                            <div className="esm-campaigns-header">
                                <span className="esm-campaigns-count">
                                    {linkedCampaigns.length} campaign{linkedCampaigns.length !== 1 ? 's' : ''} using this account
                                </span>
                            </div>

                            {campaignsLoading ? (
                                <div className="esm-campaigns-loading">
                                    <i className="fa fa-spinner fa-spin"></i> Loading campaigns...
                                </div>
                            ) : linkedCampaigns.length === 0 ? (
                                <div className="esm-campaigns-empty">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke="#d1d5db" strokeWidth="1.5"/><path d="M3 9h18" stroke="#d1d5db" strokeWidth="1.5"/><path d="M8 5V3M16 5V3" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                    <p>No campaigns linked to this account yet.</p>
                                    <button className="btn-style-new" onClick={handleAddCampaign}>
                                        <i className="fa fa-plus"></i> Create Campaign
                                    </button>
                                </div>
                            ) : (
                                <div className="esm-campaigns-list">
                                    {linkedCampaigns.map(c => {
                                        const status = (c['status'] || 'draft').toLowerCase();
                                        return (
                                            <div key={c['_id']} className="esm-campaign-row" onClick={() => handleViewCampaign(c['_id'])}>
                                                <div className="esm-campaign-info">
                                                    <span className="esm-campaign-name">{c['name'] || 'Untitled Campaign'}</span>
                                                    <div className="esm-campaign-meta">
                                                        {c['createdAt'] && (
                                                            <span className="esm-campaign-date">
                                                                {new Date(c['createdAt']).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </span>
                                                        )}
                                                        <span className={`esm-campaign-badge ${status}`}>{c['status'] || 'Draft'}</span>
                                                    </div>
                                                </div>
                                                <button className="esm-campaign-view-btn" onClick={e => { e.stopPropagation(); handleViewCampaign(c['_id']); }}>
                                                    View
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="esm-panel-footer">
                    <button className="btn-style-border" onClick={toggle}>Cancel</button>
                    <button className="btn-style-new" onClick={handleSave} disabled={saving}>
                        {saving ? <><i className="fa fa-spinner fa-spin"></i> Saving...</> : 'Save'}
                        <i className="icon ml-2">
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M5.55103 6.0199L3.8619 2.64164C3.81493 2.54733 3.79891 2.44061 3.81612 2.33667C3.83334 2.23273 3.88291 2.13687 3.95778 2.06274C4.03265 1.98862 4.129 1.94 4.23311 1.92383C4.33722 1.90765 4.44378 1.92474 4.53761 1.97265L11.5616 5.57465C11.6433 5.61681 11.7119 5.68065 11.7597 5.75919C11.8076 5.83773 11.8329 5.92792 11.8329 6.0199C11.8329 6.11187 11.8076 6.20207 11.7597 6.2806C11.7119 6.35914 11.6433 6.42299 11.5616 6.46514L4.53761 10.0671C4.44378 10.1153 4.33712 10.1326 4.23287 10.1166C4.12863 10.1005 4.03212 10.0519 3.95717 9.97767C3.88221 9.90346 3.83263 9.80745 3.81552 9.70337C3.79841 9.59929 3.81465 9.49246 3.8619 9.39816L5.55103 6.0199ZM4.52109 2.63156L6.14861 5.88716C6.19061 5.97061 6.19061 6.06918 6.14861 6.15263L4.52109 9.40824L11.1281 6.0199L4.52109 2.63156Z" fill="currentColor" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M5.88143 6.31663C5.84245 6.31664 5.80385 6.30896 5.76784 6.29404C5.73182 6.27912 5.6991 6.25726 5.67154 6.2297C5.64397 6.20213 5.62211 6.16941 5.60719 6.1334C5.59228 6.09738 5.5846 6.05879 5.5846 6.01981C5.5846 5.98082 5.59228 5.94223 5.60719 5.90621C5.62211 5.8702 5.64397 5.83748 5.67154 5.80991C5.6991 5.78235 5.73182 5.76049 5.76784 5.74557C5.80385 5.73065 5.84245 5.72298 5.88143 5.72298L11.3705 5.72298C11.4095 5.72298 11.4481 5.73065 11.4841 5.74557C11.5201 5.76049 11.5529 5.78235 11.5804 5.80991C11.608 5.83748 11.6299 5.8702 11.6448 5.90621C11.6597 5.94223 11.6674 5.98083 11.6674 6.01981C11.6674 6.05879 11.6597 6.09738 11.6448 6.1334C11.6299 6.16941 11.608 6.20213 11.5804 6.2297C11.5529 6.25726 11.5201 6.27912 11.4841 6.29404C11.4481 6.30896 11.4095 6.31664 11.3705 6.31664L5.88143 6.31663Z" fill="currentColor" />
                            </svg>
                        </i>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailSettingsModal;
